/** @format */

import { DAY_MIN, INNER, PAD, ROW_H, SNAP, getDayS } from './constants';

export function t2m(t) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

export function m2t(m) {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

export function addHoursToTime(start, hours) {
  const total = t2m(start) + Math.round(Number(hours) * 60);
  return m2t(total);
}

export function d2s(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${dy}`;
}

/** True when the calendar day is strictly before today (local time). */
export function isPastPlanningDay(dateStr) {
  if (!dateStr) return false;
  return dateStr < d2s(new Date());
}

export function s2d(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

const PLANNING_TIMEZONE = 'Europe/Paris';

/**
 * UTC instant for Google Routes `departureTime` from a planning day + wall clock.
 * @param {string} dateStr YYYY-MM-DD
 * @param {number} minutesFromMidnight minutes in Europe/Paris
 * @returns {Date | null}
 */
export function planningDepartureDate(dateStr, minutesFromMidnight) {
  if (!dateStr) return null;
  const clamped = Math.max(
    0,
    Math.min(24 * 60 - 1, Math.round(minutesFromMidnight)),
  );
  const hours = Math.floor(clamped / 60);
  const mins = clamped % 60;
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return null;

  let utcMs = Date.UTC(year, month - 1, day, hours, mins, 0);
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: PLANNING_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  for (let attempt = 0; attempt < 4; attempt += 1) {
    const parts = Object.fromEntries(
      formatter
        .formatToParts(new Date(utcMs))
        .map((part) => [part.type, part.value]),
    );
    const parisDay = `${parts.year}-${parts.month}-${parts.day}`;
    const parisMinutes = Number(parts.hour) * 60 + Number(parts.minute);
    const targetMinutes = hours * 60 + mins;
    let diffMinutes = targetMinutes - parisMinutes;
    if (parisDay < dateStr) diffMinutes += 24 * 60;
    if (parisDay > dateStr) diffMinutes -= 24 * 60;
    if (diffMinutes === 0) break;
    utcMs += diffMinutes * 60 * 1000;
  }

  const departure = new Date(utcMs);
  return Number.isNaN(departure.getTime()) ? null : departure;
}

/** Travel duration: "45 min" below 1h, else "1h15" (Hhmm). */
export function formatTravelDurationMinutes(minutes) {
  const total = Math.round(Number(minutes));
  if (!Number.isFinite(total) || total < 0) return '—';
  if (total < 60) return `${total} mins`;
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return `${hours}h${String(mins).padStart(2, '0')}`;
}

/** French short date for planning list chips (matches ChantierPopup). */
export function formatPlanningChipDate(dateStr) {
  if (!dateStr) return '';
  return s2d(dateStr).toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

export function snapMin(m) {
  return Math.round(m / SNAP) * SNAP;
}

export function min2px(min, inner = INNER) {
  return Math.round(
    PAD + (Math.max(0, Math.min(min - getDayS(), DAY_MIN)) / DAY_MIN) * inner,
  );
}

export function dur2px(dur, inner = INNER) {
  return Math.round((Math.min(Math.max(0, dur), DAY_MIN) / DAY_MIN) * inner);
}

export function ivLayout(p, rowMetrics = null) {
  const rowH = rowMetrics?.rowH ?? ROW_H;
  const inner = rowMetrics?.inner ?? INNER;
  const start = t2m(p.startTime);
  const end = t2m(p.endTime);
  const top = min2px(start, inner);
  const maxH = rowH - PAD - top;
  const height = Math.max(22, Math.min(dur2px(end - start, inner), maxH));
  return { top, height };
}

function startOfPlanningWeekMonday(referenceDate) {
  const date = new Date(referenceDate);
  const dow = date.getDay() === 0 ? 6 : date.getDay() - 1;
  const monday = new Date(date);
  monday.setDate(date.getDate() - dow);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

export function getWeekDates(weekOff = 0) {
  const today = new Date();
  const mon = startOfPlanningWeekMonday(today);
  mon.setDate(mon.getDate() + weekOff * 7);
  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(mon);
    d.setDate(mon.getDate() + i);
    return d;
  });
}

/** Week offset (relative to the current week) for the week that contains `date`. */
export function weekOffForDate(date) {
  if (!date) return 0;
  const currentMonday = startOfPlanningWeekMonday(new Date());
  const targetMonday = startOfPlanningWeekMonday(date);
  const diffDays = Math.round(
    (targetMonday.getTime() - currentMonday.getTime()) / (24 * 60 * 60 * 1000),
  );
  return Math.round(diffDays / 7);
}
